import { get, isEmpty, isNil } from 'lodash';
import { RefObject } from 'react';
import { IntlShape } from 'react-intl';
import { toast } from 'react-toastify';
import byteSize from 'byte-size';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';

import * as utils from 'containers/IdentificationPage/SearchResults/ClaimWithNotIndexedPV/utils';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import { translate } from 'containers/services/translate';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { FILE_LIMIT, FILE_TYPE } from 'containers/UploadDocuments/constants';
import messages from 'containers/UploadDocuments/messages';
import { JudgementADDDocumentType } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { CivilParty } from 'containers/LawyerLegalProceedingsPage/judgementAdd/CivilParty/constants';
import { PROCEDURE_STATUSES } from 'containers/data-structure';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { PROCEDURE_STEPS } from 'containers/data-structure';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { IJudgementAdd } from 'containers/LawyerLegalProceedingsPage/typings';
import {
  filterVehicle,
  filterVictim,
} from 'containers/CourtManagerLegalProceedings/utils';
import { isPendingApprovalClaim } from 'containers/LawyerLegalProceedingsPage/utils';
import { FeeNoteType, VictimType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';
import { GLOBAL_STATUS, OPENING_STATE } from 'services/constants';
export const uploadClick = (fileRef: RefObject<HTMLInputElement>) => () => {
  fileRef.current?.click();
};

export const fileSelectHandler = (
  uploadDocument: Function,
  props: {
    intl: IntlShape;
    setDocumentId?: Function;
    docId?: string;
    setText?: Function;
    recognize?: Function;
  },
  shouldGetBase64?: boolean,
) => async (event: any) => {
  const [file] = event.target.files || event.dataTransfer.files;

  if (file.size < FILE_LIMIT && file.type === FILE_TYPE) {
    if (shouldGetBase64) {
      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result as string; // ← Base64 string
        uploadDocument(base64); // ← Pass Base64 to parent
        props.setDocumentId && props.setDocumentId(props.docId);
      };

      reader.readAsDataURL(file); // ← convert to Base64
    } else {
      uploadDocument(file);
      props.setDocumentId && props.setDocumentId(props.docId);
    }
  } else {
    const { value, unit } = byteSize(FILE_LIMIT);
    toast.error(
      translate(messages.errorUploadDocumentSizeAndType, props, {
        size: `${~~value}${unit}`,
      }),
    );
  }
  if (props.setText && props.recognize) {
    const extractedText = await props.recognize(file);
    props.setText(extractedText);
  }
  const list = utils.emptyFile();
  event.target.files = list.files;
};

export const isChecked = (victimId: number, victims: VictimType[]) => {
  if (isEmpty(victims)) {
    return false;
  }
  const victim = victims.find(v => v.id === victimId);
  return !isEmpty(victim);
};

export const formatCourtFileNumber = (courtFileNumber: any) => {
  if (!courtFileNumber) return undefined;
  const parts = courtFileNumber?.split('/');
  let leftSide = parts[0];
  if (leftSide?.length < 7) {
    leftSide = leftSide?.padStart(7, '0');
    const middlePart = parts[1] ? `/${parts[1]}` : '';
    const rightPart = parts[2] ? `/${parts[2]}` : '';

    return leftSide + middlePart + rightPart;
  } else {
    return courtFileNumber;
  }
};

export const judgementAddFormator = (
  claimId: number,
  details: any,
  judgementAddDocuments: JudgementADDDocumentType[],
  civilPartyDocuments: CivilParty[],
  listing: any,
  policyInfo: any,
  isCourtManager: boolean,
) => {
  if (isEmpty(details.convictionId)) {
    return {
      claimId,
      step: PROCEDURE_STEPS.JUDGEMENT_ADD,
      status:
        (isCourtManager &&
          !userPrivileges.userHasPrivilege(
            PRIVILEGES.ISCOURTMONITORINGMANAGER,
          )) ||
        (userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMONITORINGMANAGER) &&
          userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER))
          ? PROCEDURE_STATUSES.VALIDATED
          : PROCEDURE_STATUSES.PENDING,
      civilDocuments: civilPartyDocuments,
      judgementAddDocuments: judgementAddDocuments.map(
        (item: JudgementADDDocumentType) => ({
          ...item,
          status: isCourtManager ? PROCEDURE_STATUSES.VALIDATED : item.status,
        }),
      ),
      details: {
        courtFile: formatCourtFileNumber(details.courtFile),
        isInsuredSued: details.isInsuredSued,
        jurisdictionId: details.jurisdictionId,
        packageContinuation: details.packageContinuation,
        procedureNatureId: details.procedureNatureId,
        hearingDate: details.hearingDate,
        suedParties: details.suedParties,
      },
      username:
        userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMONITORINGMANAGER) ||
        isCourtManager
          ? currentUserStorage.get().username
          : currentIntervenantStorage?.get()?.preferred_username,
      ...(isCourtManager
        ? {
            validator: currentUserStorage.get().username,
          }
        : undefined),
    };
  } else {
    return {
      claimId,
      step: PROCEDURE_STEPS.JUDGEMENT_ADD,
      status: PROCEDURE_STATUSES.PENDING,
      civilDocuments: civilPartyDocuments,
      judgementAddDocuments,
      details,
      username: currentIntervenantStorage?.get()?.preferred_username,
    };
  }
};
export const toBeEdited = (status: string) =>
  status === PROCEDURE_STATUSES.TO_EDIT;

export const getVictims = (
  isCivilProceeding: boolean,
  currentJudiciaryProcess: any,
  judgementAdd: IJudgementAdd,
  victims: VictimType[],
  claimRecap: ClaimRecapType,
) => {
  return (isCivilProceeding && isEmpty(currentJudiciaryProcess)) ||
    (isEmpty(judgementAdd) && !isCivilProceeding)
    ? isPendingApprovalClaim(claimRecap) ||
      (featureService.hasFeature(FEATURES.OPENING_BY_USAGE) &&
        get(claimRecap, 'claim.openingState', null) ===
          OPENING_STATE.NOT_COMPLETED)
      ? victims
      : filterVictim(victims)
    : isCivilProceeding && !isEmpty(currentJudiciaryProcess)
    ? isPendingApprovalClaim(claimRecap) ||
      (featureService.hasFeature(FEATURES.OPENING_BY_USAGE) &&
        get(claimRecap, 'claim.openingState', null) ===
          OPENING_STATE.NOT_COMPLETED)
      ? get(currentJudiciaryProcess, 'victims', [])
      : filterVictim(get(currentJudiciaryProcess, 'victims', []))
    : filteredCivilDocumentVictims(
        get(judgementAdd, 'civilDocuments', []),
        isPendingApprovalClaim(claimRecap),
      );
};
export const filteredCivilDocumentVictims = (
  civilDocuments: any,
  isPendingApprovalClaim: boolean,
) => {
  const uniqueVictims = {} as any;
  const filteredVictims: any[] = [];

  civilDocuments.forEach((civilDocument: any) => {
    civilDocument.victims.forEach((victim: VictimType) => {
      if (
        isPendingApprovalClaim &&
        !uniqueVictims[(victim.id as unknown) as number]
      ) {
        uniqueVictims[(victim.id as unknown) as number] = true;
        filteredVictims.push(victim);
      } else if (
        !uniqueVictims[(victim.id as unknown) as number] &&
        !isPendingApprovalClaim &&
        !isNil(victim.victimKey)
      ) {
        uniqueVictims[(victim.id as unknown) as number] = true;
        filteredVictims.push(victim);
      }
    });
  });
  return filteredVictims;
};

export const filteredCivilDocumentVictimsClaim = (
  civilDocuments: any,
  claimVictims: VictimType[],
  isPendingApprovalClaim: boolean,
) => {
  const uniqueVictims = {} as any;
  const filteredVictims: any[] = [];
  claimVictims.forEach((item: VictimType) => {
    civilDocuments.forEach((civilDocument: any) => {
      civilDocument.victims.forEach((victim: VictimType) => {
        if (
          !uniqueVictims[(victim.id as unknown) as number] &&
          !isNil(victim.victimKey) &&
          !isPendingApprovalClaim &&
          victim.id === item.id
        ) {
          uniqueVictims[(victim.id as unknown) as number] = true;
          filteredVictims.push(item);
        } else if (
          !uniqueVictims[(victim.id as unknown) as number] &&
          victim.id === item.id
        ) {
          uniqueVictims[(victim.id as unknown) as number] = true;
          filteredVictims.push(item);
        }
      });
    });
  });
  return filteredVictims;
};
export const getVehicles = (
  isCivilProceeding: boolean,
  currentJudiciaryProcess: any,
  judgementAdd: IJudgementAdd,
  vehicules: Vehicule[],
) => {
  return (isCivilProceeding && isEmpty(currentJudiciaryProcess)) ||
    isEmpty(judgementAdd)
    ? filterVehicle(vehicules)
    : isCivilProceeding && !isEmpty(currentJudiciaryProcess)
    ? filterVehicle(get(currentJudiciaryProcess, 'vehicules', []))
    : filterVehicle(vehicules);
};

export const validationCourtFileNumber = (courtFileNumber: any) => {
  let pattern = /^\d{1,7}\/\d{4}\/\d{4}$/;
  return pattern.test(courtFileNumber);
};

export const getDocumentId = (index: any) => {
  return 'Invoice' + index;
};

export const getVictimsIncome = (
  isCivilProceeding: boolean,
  currentJudiciaryProcess: any,
  judgementAdd: IJudgementAdd,
  victims: VictimType[],
  claimRecap: ClaimRecapType,
) => {
  return isCivilProceeding && isEmpty(currentJudiciaryProcess)
    ? isPendingApprovalClaim(claimRecap)
      ? victims
      : filterVictim(victims)
    : isCivilProceeding && !isEmpty(currentJudiciaryProcess)
    ? isPendingApprovalClaim(claimRecap)
      ? get(currentJudiciaryProcess, 'victims', [])
      : filterVictim(get(currentJudiciaryProcess, 'victims', []))
    : !isEmpty(get(judgementAdd, 'civilDocuments', []))
    ? filteredCivilDocumentVictimsClaim(
        get(judgementAdd, 'civilDocuments', []),
        victims,
        isPendingApprovalClaim(claimRecap),
      )
    : [];
};

export const getPendingFeeNotesByStep = (
  feeNotes: FeeNoteType[],
  step: string,
  judgementId: number,
  judgementAddDocumentsIds: any,
  codeIntervenant?: string,
) => {
  return feeNotes.filter(
    (el: FeeNoteType) =>
      el.step === step &&
      (el.status === GLOBAL_STATUS.PENDING ||
        el.status === GLOBAL_STATUS.REJECTED) &&
      (!isNil(codeIntervenant)
        ? el.codeIntervenant === codeIntervenant
        : true) &&
      (!isNil(el.judgementId) ? el?.judgementId === judgementId : true) &&
      (!isEmpty(judgementAddDocumentsIds) && !isNil(el.judgementAddDocumentId)
        ? judgementAddDocumentsIds.includes(el.judgementAddDocumentId)
        : true),
  );
};
